import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import dataApi from '@/shared/services/dataApi';
import engineApi from '@/shared/services/engineApi';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import { useChatPageState } from '@/shared/contexts/ChatPageContext';
import isSafeUrl from '@/shared/utils/urlHelpers';
import { formatDate, formatRelativeDate } from '@/shared/utils/dateHelpers';
import GeoBoundaryMap from '@/components/common/GeoBoundaryMap';
import ShareToCommunityButton from '@/components/common/ShareToCommunityButton';
import TemporalRangeBar from '@/components/common/TemporalRangeBar';
import InfoTooltip from '@/components/common/InfoTooltip';
import SEO from '@/components/common/SEO';

export default function DataSourceDetail() {
    const { id } = useParams();
    const { isAdmin } = useAuth();
    const [source, setSource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { openChat } = useChatPageState();

    // Admin action state
    const [validating, setValidating] = useState(false);
    const [indexing, setIndexing] = useState(false);
    const [actionResult, setActionResult] = useState(null);

    // Publish source detail context so Bili knows what the user is viewing
    // focusedDataSource includes the id so Bili can query it directly
    useChatPageEffect(
        {
            page: 'data-source-detail',
            section: 'data-discovery',
            focusedDataSource: {
                id: source?.id,
                name: source?.name,
                apiType: source?.api_type,
            },
            sourceName: source?.name,
            organization: source?.organization,
            spotlight: source?.spotlight,
            categories: source?.category,
            keywords: source?.keywords,
            apiType: source?.api_type,
            updateFrequency: source?.update_frequency,
        },
        [source?.id]
    );

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const data = await dataApi.getSource(id);
                if (!cancelled) setSource(data);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const handleValidate = async () => {
        setValidating(true);
        setActionResult(null);
        try {
            const result = await engineApi.post(`/admin/validate-source/${id}`);
            setActionResult({
                variant: 'success',
                message: `Validation complete: ${result.status} (HTTP ${result.status_code})`,
            });
            // Refresh to show updated status
            const updated = await dataApi.getSource(id);
            setSource(updated);
        } catch (err) {
            setActionResult({
                variant: 'danger',
                message: err.message || 'Validation failed',
            });
        } finally {
            setValidating(false);
        }
    };

    const handleIndex = async () => {
        setIndexing(true);
        setActionResult(null);
        try {
            const result = await engineApi.post(`/admin/sources/${id}/index`);
            setActionResult({
                variant: 'success',
                message: result.message || 'Re-indexed successfully',
            });
        } catch (err) {
            setActionResult({
                variant: 'danger',
                message: err.message || 'Indexing failed',
            });
        } finally {
            setIndexing(false);
        }
    };

    if (loading) {
        return (
            <section className="page-container bg-light">
                <Container className="py-5 text-center">
                    <Spinner animation="border" variant="primary" />
                    <span className="visually-hidden">Loading...</span>
                </Container>
            </section>
        );
    }

    if (error || !source) {
        return (
            <section className="page-container bg-light">
                <Container className="py-5">
                    <Alert variant="danger">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error || 'Data source not found.'}</p>
                        <Link to="/data" className="btn btn-outline-primary">
                            Back to Discovery
                        </Link>
                    </Alert>
                </Container>
            </section>
        );
    }

    const categories = source.category || [];
    const keywords = source.keywords || [];
    return (
        <section className="page-container bg-light">
            <SEO title={source?.name || 'Data Source'} />
            <Container className="py-4">
                {/* Breadcrumb */}
                <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                            <Link to="/data">Data Discovery</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to="/data?tab=sources">Sources</Link>
                        </li>
                        <li
                            className="breadcrumb-item active"
                            aria-current="page"
                        >
                            {source.name}
                        </li>
                    </ol>
                </nav>

                <Row className="g-4">
                    {/* Main content */}
                    <Col lg={8}>
                        <h1 className="h3 text-primary fw-bold mb-3">
                            {source.name}
                        </h1>

                        {source.description && (
                            <p className="text-body-secondary fs-5 mb-4">
                                {source.description}
                            </p>
                        )}

                        {/* Geographic & Temporal Coverage */}
                        {(source.geospatial_bound_description ||
                            source.earliest_date ||
                            source.newest_date) && (
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <Card.Title className="h6 text-primary mb-3">
                                        Coverage
                                    </Card.Title>
                                    {source.geospatial_bound_description && (
                                        <div className="mb-3">
                                            <strong className="text-muted d-block mb-2 small">
                                                Geographic Coverage
                                                <InfoTooltip text="The geographic area this data describes. The map and text below show its boundary." />
                                            </strong>
                                            <GeoBoundaryMap
                                                description={
                                                    source.geospatial_bound_description
                                                }
                                                geojson={
                                                    source.geospatial_bound
                                                }
                                                className="mb-2"
                                            />
                                            <small className="text-muted">
                                                {
                                                    source.geospatial_bound_description
                                                }
                                            </small>
                                        </div>
                                    )}
                                    {(source.earliest_date ||
                                        source.newest_date) && (
                                        <div>
                                            <strong className="text-muted d-block mb-2 small">
                                                Temporal Coverage
                                                <InfoTooltip text="The span of time this data covers, from its earliest record to its most recent. An Ongoing label means it is still being updated." />
                                            </strong>
                                            <TemporalRangeBar
                                                earliestDate={
                                                    source.earliest_date
                                                }
                                                newestDate={source.newest_date}
                                            />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* URL Template (power user info) */}
                        {source.url_template && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    URL Template
                                </strong>
                                <code className="small">
                                    {source.url_template}
                                </code>
                            </div>
                        )}

                        {/* Categories */}
                        {categories.length > 0 && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    Categories
                                </strong>
                                <div className="d-flex flex-wrap gap-1">
                                    {categories.map((cat) => (
                                        <Badge
                                            key={cat}
                                            bg="primary"
                                            className="fw-normal"
                                        >
                                            {cat}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Keywords */}
                        {keywords.length > 0 && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    Keywords
                                </strong>
                                <div className="d-flex flex-wrap gap-1">
                                    {keywords.map((kw) => (
                                        <Badge
                                            key={kw}
                                            bg="light"
                                            text="dark"
                                            className="fw-normal"
                                        >
                                            {kw}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {source.notes && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    Notes
                                </strong>
                                <p className="text-body-secondary mb-0">
                                    {source.notes}
                                </p>
                            </div>
                        )}
                    </Col>

                    {/* Sidebar */}
                    <Col lg={4}>
                        {/* Organization card */}
                        {source.organization && (
                            <Card className="border-0 shadow-sm mb-3">
                                <Card.Body>
                                    <Card.Title className="h6 text-primary">
                                        Organization
                                    </Card.Title>
                                    <p className="mb-0 fw-medium">
                                        {source.organization}
                                    </p>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Metadata card */}
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body>
                                <Card.Title className="h6 text-primary mb-3">
                                    Metadata
                                </Card.Title>
                                <dl className="mb-0">
                                    {source.api_type && (
                                        <>
                                            <dt className="small text-muted">
                                                API Type
                                            </dt>
                                            <dd>
                                                <Badge bg="info">
                                                    {source.api_type}
                                                </Badge>
                                            </dd>
                                        </>
                                    )}
                                    {source.sustainability_pillar && (
                                        <>
                                            <dt className="small text-muted">
                                                Sustainability Pillar
                                            </dt>
                                            <dd className="text-capitalize">
                                                {source.sustainability_pillar}
                                            </dd>
                                        </>
                                    )}
                                    {source.update_frequency && (
                                        <>
                                            <dt className="small text-muted">
                                                Update Frequency
                                            </dt>
                                            <dd>{source.update_frequency}</dd>
                                        </>
                                    )}
                                    {source.data_origin && (
                                        <>
                                            <dt className="small text-muted">
                                                Data Origin
                                            </dt>
                                            <dd>{source.data_origin}</dd>
                                        </>
                                    )}
                                    {source.scale_extent && (
                                        <>
                                            <dt className="small text-muted">
                                                Scale
                                            </dt>
                                            <dd>{source.scale_extent}</dd>
                                        </>
                                    )}
                                    <dt className="small text-muted">Status</dt>
                                    <dd>
                                        <Badge
                                            bg={
                                                source.status === 'active'
                                                    ? 'success'
                                                    : source.status ===
                                                        'deprecated'
                                                      ? 'warning'
                                                      : 'secondary'
                                            }
                                        >
                                            {source.status}
                                        </Badge>
                                    </dd>
                                </dl>
                            </Card.Body>
                        </Card>

                        {/* Validation & Audit card */}
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body>
                                <Card.Title className="h6 text-primary mb-3">
                                    Validation &amp; Audit
                                </Card.Title>
                                <dl className="mb-0">
                                    {source.last_validated && (
                                        <>
                                            <dt className="small text-muted">
                                                Last Validated
                                            </dt>
                                            <dd className="small">
                                                {formatRelativeDate(
                                                    source.last_validated
                                                )}
                                            </dd>
                                        </>
                                    )}
                                    {source.created_at && (
                                        <>
                                            <dt className="small text-muted">
                                                Created
                                            </dt>
                                            <dd className="small">
                                                {formatDate(source.created_at)}
                                            </dd>
                                        </>
                                    )}
                                    {source.updated_at && (
                                        <>
                                            <dt className="small text-muted">
                                                Last Updated
                                            </dt>
                                            <dd className="small">
                                                {formatDate(source.updated_at)}
                                            </dd>
                                        </>
                                    )}
                                </dl>
                            </Card.Body>
                        </Card>

                        {/* Links */}
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body className="d-flex flex-column gap-2">
                                {source.base_url &&
                                    isSafeUrl(source.base_url) && (
                                        <Button
                                            href={source.base_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="primary"
                                            className="rounded-pill"
                                        >
                                            View Data Source
                                        </Button>
                                    )}
                                <Button
                                    variant="outline-primary"
                                    className="rounded-pill"
                                    onClick={() =>
                                        openChat(
                                            `Tell me about the ${source.name} dataset`
                                        )
                                    }
                                >
                                    Ask Bili About This
                                </Button>
                                <ShareToCommunityButton
                                    variant="button"
                                    shareContent={{
                                        postType: 'resource',
                                        title: source.name,
                                        content: source.description,
                                        resourceUrl: source.base_url,
                                        resourceType: 'dataset',
                                    }}
                                />
                                {source.spotlight && (
                                    <Link
                                        to={`/spotlights/${source.spotlight}`}
                                        className="btn btn-outline-dark rounded-pill"
                                    >
                                        {source.spotlight
                                            .charAt(0)
                                            .toUpperCase() +
                                            source.spotlight.slice(1)}{' '}
                                        Spotlight
                                    </Link>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Admin Actions (conditional) */}
                        {isAdmin && (
                            <Card className="border-0 shadow-sm mb-3 border-start border-primary border-3">
                                <Card.Body>
                                    <Card.Title className="h6 text-primary mb-3">
                                        Admin Actions
                                    </Card.Title>
                                    {actionResult && (
                                        <Alert
                                            variant={actionResult.variant}
                                            className="small py-2 px-3"
                                            dismissible
                                            onClose={() =>
                                                setActionResult(null)
                                            }
                                        >
                                            {actionResult.message}
                                        </Alert>
                                    )}
                                    <div className="d-flex flex-column gap-2">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidate}
                                            disabled={validating || indexing}
                                        >
                                            {validating ? (
                                                <>
                                                    <Spinner
                                                        animation="border"
                                                        size="sm"
                                                        className="me-1"
                                                    />
                                                    Validating...
                                                </>
                                            ) : (
                                                'Validate URL'
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleIndex}
                                            disabled={validating || indexing}
                                        >
                                            {indexing ? (
                                                <>
                                                    <Spinner
                                                        animation="border"
                                                        size="sm"
                                                        className="me-1"
                                                    />
                                                    Indexing...
                                                </>
                                            ) : (
                                                'Re-index'
                                            )}
                                        </Button>
                                        <Link
                                            to="/admin/data-sources"
                                            className="btn btn-link btn-sm text-start text-primary p-0"
                                        >
                                            View Admin Hub
                                        </Link>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Container>
        </section>
    );
}
